import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useParams, useSearchParams} from "react-router";
import {Template} from "../../../../components/template/template.jsx";
import {useQuery} from "@tanstack/react-query";
import citizenService from "../../../../services/web/citizen-service.js";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {AgGridReact} from "ag-grid-react";
import {Card} from "../../../../components/card/card.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import CitizenHistoryActions from "./utils/citizen-history-actions.js";
import InputForm from "../../../../components/input/input-form.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaFileAlt, FaPlus, FaPrint, FaSearch} from "react-icons/fa";
import InputDatetime from "../../../../components/input/input-datetime.jsx";
import CitizenHistoryModal from "./components/citizen-history-modal.jsx";
import citizenHistoryService from "../../../../services/web/citizen-history-service.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import useReportFiltersFetch from "../../../../hooks/use-report-filters-fetch.js";

const BASENAME = import.meta.env.BASE_URL;

const initialFilters = {
    history_actions: "",
    dtStart: "",
    dtEnd: "",
    createdById: "",
    description: "",
    target: ""
}

const initialHistory = {
    aggressorId: "citizen",
    action: "",
    description: "",
    dtAction: "",
    executionUserId : ""
}

const CitizenHistory = () => {
    const {id} = useParams();
    const [searchParams] = useSearchParams();    const [filters, setFilters] = useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);

    const navigate = navigateHook();

    const [history, setHistory] = useState(initialHistory);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (localStorage.getItem("open-history")) {
            setOpen(true);
            localStorage.removeItem("open-history")
        }
    }, []);

    const {data: citizen, isSuccess} = useQuery({
        queryFn: () => citizenService.get(id),
        queryKey: ["citizen"]
    });

    const {data: dataFetch, isLoading, refetch} = useQuery({
        queryFn: () => citizenHistoryService.list(id, appliedFilters),
        queryKey: ["history", id, appliedFilters],
        enabled: isSuccess
    });

    const {sosHistoryCreatedBy} = useReportFiltersFetch({fetchSosHistoryCreatedBy: true});

    useEffect(() => {
        if (!dataFetch || !searchParams.has("history_id")) return;

        const hisotryId = searchParams.get("history_id");
        setOpen(true);
        setHistory(dataFetch.find(h => h.id?.toString() === hisotryId))
    }, [dataFetch, searchParams]);

    const gridRef = useRef(undefined);

    const defaultColDef = useMemo(() => ({resizable: false}), []);

    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    const isExternalFilterPresent = useCallback(() => Boolean(appliedFilters.createdById), [appliedFilters.createdById]);

    const doesExternalFilterPass = useCallback((node) => {
        if (!appliedFilters.createdById) return true;

        return String(node.data?.usernameId ?? "") === String(appliedFilters.createdById);
    }, [appliedFilters.createdById]);

    useEffect(() => {
        gridRef.current?.api?.onFilterChanged();
    }, [appliedFilters.createdById]);

    useEffect(() => {
        const openModal = localStorage.getItem("openHistoryModal");
        if (openModal) {
            setOpen(true);
            localStorage.removeItem("openHistoryModal");
        }
    }, []);
    const [columnDefs,] = useState([
        { field: "dtAction", headerName: "Data/Hora", comparator: aggridUtils.agGridCompareDateTime, sort: "desc" },
        { field: "target", headerName: "Cidadão/Empresa", flex: 1, comparator: aggridUtils.nullLastComparator },
        { field: "action", headerName: "Ação", flex: 1, valueFormatter: ({value}) => CitizenHistoryActions.toView(value), comparator: aggridUtils.nullLastComparator },
        { field: "username", headerName: "Cadastrado por", flex: 1, comparator: aggridUtils.nullLastComparator },
        { field: "executionUser", headerName: "Realizado por", flex: 1, comparator: aggridUtils.nullLastComparator },
        { field: "description", headerName: "Descrição", flex: 1, comparator: aggridUtils.nullLastComparator }
    ])

    const handleInput = ({target: {id, value}}) => setFilters(prev => ({...prev, [id]: value}));

    const search = useCallback(() => setAppliedFilters({...filters}), [filters]);

    const clear = useCallback(() => {
        setFilters(initialFilters);
        setAppliedFilters(initialFilters);
    }, [])

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clear);

    const addHistory = () => {
        setHistory(initialHistory)
        setOpen(true)
    }

    const onCellClicked = ({data}) => {
        setHistory(data)
        setOpen(true)
    };

    const openReport = () => {
        const historyDates = (dataFetch ?? [])
            .map((item) => new Date(item.dtActionUTC ?? item.dtAction))
            .filter((date) => !Number.isNaN(date.getTime()));
        const firstHistoryDate = historyDates.length ? new Date(Math.min(...historyDates)) : null;
        const lastHistoryDate = historyDates.length ? new Date(Math.max(...historyDates)) : null;

        const filtersToReport = {
            dtStart: appliedFilters.dtStart || firstHistoryDate,
            dtEnd: appliedFilters.dtEnd || lastHistoryDate,
            actions: appliedFilters.history_actions ? [appliedFilters.history_actions] : [],
            createdByIds: appliedFilters.createdById ? [appliedFilters.createdById] : [],
            citizenId: id
        };

        const encodedFilters = btoa(encodeURIComponent(JSON.stringify(filtersToReport)).replace(/%([0-9A-F]{2})/g, (_, code) => String.fromCharCode(parseInt(code, 16))));
        navigate(`reports/sos_history?d=${encodeURIComponent(encodedFilters)}`, true);
    };

    return (
        <>
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>Histórico {citizen ? "- " + citizen.name : ""}</Template.Title>

                    <div className={"flex gap-2"}>
                        <Button title="Acessar Histórico Operacional do SOS" onClick={openReport}>
                            <FaFileAlt/><span className="font-bold">Relatório</span>
                        </Button>
                        <Button onClick={addHistory}>
                            <FaPlus/> Adicionar
                        </Button>
                    </div>
                </Template.Header>

                <Template.Body className={"space-y-2"}>
                    <Card>
                        <Card.Body className={"grid grid-cols-12 gap-4"}>
                            <SelectComponent
                                classNameWrapper={"col-span-2"}
                                value={CitizenHistoryActions.toOption(filters.history_actions)}
                                options={CitizenHistoryActions.options}
                                label={"Ação"}
                                onChange={(value) => setFilters(prev => ({...prev, history_actions: value?.value}))}
                            />

                            <InputDatetime
                                classNameWrapper={"col-span-2"}
                                id={"dtStart"}
                                label={"Data inicial"}
                                selected={filters.dtStart}
                                dateFormat="LL"
                                onChange={(date) => setFilters(prev => ({...prev, dtStart: date }))}
                            />

                            <InputDatetime
                                classNameWrapper={"col-span-2"}
                                id={"dtEnd"}
                                label={"Data final"}
                                selected={filters.dtEnd}
                                dateFormat="LL"
                                onChange={(date) => setFilters(prev => ({...prev, dtEnd: date }))}
                            />

                            <InputForm
                                classNameWrapper={"col-span-2"}
                                id={"target"}
                                label={"Cidadão/Empresa"}
                                value={filters.target}
                                onChange={handleInput}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-2"}
                                id="createdById"
                                label={"Cadastrado por"}
                                value={filters.createdById}
                                options={sosHistoryCreatedBy}
                                onChange={(value) => setFilters(prev => ({...prev, createdById: value?.value ?? ""}))}
                                isClearable={true}
                            />

                            <div className={"col-span-2 flex justify-between gap-2"}>
                                <InputForm
                                    classNameWrapper={"w-full"}
                                    id={"description"}
                                    label={"Descrição"}
                                    value={filters.description}
                                    onChange={handleInput}
                                />

                                <Button target={"default"} onClick={clear}>
                                    <FaEraser />
                                </Button>

                                <Button onClick={search}>
                                    <FaSearch />
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Body>
                            <div className={"ag-theme-quartz grid"} style={{height: "calc(100vh - 153px)"}}>
                                <AgGridReact
                                    ref={gridRef}
                                    rowHeight={32}
                                    rowData={dataFetch ?? []}
                                    columnDefs={columnDefs}
                                    defaultColDef={defaultColDef}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    onCellClicked={onCellClicked}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    theme={theme}
                                    isExternalFilterPresent={isExternalFilterPresent}
                                    doesExternalFilterPass={doesExternalFilterPass}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <CitizenHistoryModal
                id={id}
                citizen={citizen}
                open={open}
                setOpen={setOpen}
                history={history}
                setHistory={setHistory}
                refetch={refetch}
            />
        </>
    );
};

export default CitizenHistory;